/**
 * batch-67 middelen-inname (FO-499 + FO-505) - gedeelde types van het
 * middelen-ledger, het uitgifte-/innameformulier, de overdracht en de
 * instellingen. Gedeeld door API (`.safeParse`), backoffice en portal
 * (client-side validatie met hetzelfde schema, inline veldfouten).
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import { amsterdamVandaagYmd } from './inkoop'
import { OFFBOARDING_INLEVER_STATEN, type OffboardingInleverStaat } from './offboarding'
import type { VasteInhoudRegelDTO } from './voorraad'

// ─── Enums (spiegel van de Prisma-enums; de drift-test bewaakt de ledger) ────

export const MIDDEL_TYPES = ['VOORRAAD_UNIEK_ITEM', 'UITRUSTING_ITEM', 'VOERTUIG', 'BULK_ARTIKEL', 'TANKPAS'] as const
export type MiddelType = (typeof MIDDEL_TYPES)[number]
export const MIDDEL_TYPE_LABELS: Record<MiddelType, string> = {
  VOORRAAD_UNIEK_ITEM: 'Apparatuur / gereedschap',
  UITRUSTING_ITEM: 'Kleding / PBM',
  VOERTUIG: 'Voertuig',
  BULK_ARTIKEL: 'Voorraadartikel',
  TANKPAS: 'Tankpas',
}

// batch-69 samenstellen (FO-510) — EIGENDOM: uitgegeven als eigendom van de
// medewerker (welkomstpakket). Telt nooit als bezit: geen bruikleen, geen inname.
export const MIDDEL_MUTATIE_SOORTEN = ['UITGIFTE', 'INNAME', 'OVERDRACHT', 'WISSEL', 'AFBOEKING', 'STATUSWIJZIGING', 'EIGENDOM'] as const
export type MiddelMutatieSoort = (typeof MIDDEL_MUTATIE_SOORTEN)[number]
export const MIDDEL_MUTATIE_SOORT_LABELS: Record<MiddelMutatieSoort, string> = {
  UITGIFTE: 'Uitgegeven',
  INNAME: 'Ingenomen',
  OVERDRACHT: 'Overgedragen',
  WISSEL: 'Gewisseld',
  AFBOEKING: 'Afgeboekt',
  STATUSWIJZIGING: 'Status gewijzigd',
  EIGENDOM: 'Uitgegeven als eigendom',
}

export const MIDDEL_MUTATIE_BRONNEN = [
  'HANDMATIG',
  'ONBOARDING',
  'OFFBOARDING',
  'BRUIKLEEN',
  'VOERTUIGWISSEL',
  'LEVERING',
  'IMPORT',
  'KIST',
  'TAAK',
  'FORMULIER',
  'REPARATIE',
  'SYSTEEM',
  // batch-69 samenstellen (FO-509) - uitgifte vanuit de samenstel-opdracht.
  'SAMENSTELLING',
  // batch-69 samenstellen (FO-510) - welkomstpakket en koffer als complete set.
  'WELKOMST',
  'KOFFER_SET',
  // batch-69 samenstellen (FO-516) - afgevinkt op de inname-afvinklijst.
  'INNAME_SESSIE',
  // batch-69 magazijn-cijfers (FO-520 §C12) — doorgevoerd vanuit een telsessie.
  'TELLING',
  // batch-70 uitrusting-controle (FO-531 §E) - afgehandeld vanuit de uitrustingscontrole.
  'CAMPAGNE',
] as const
export type MiddelMutatieBron = (typeof MIDDEL_MUTATIE_BRONNEN)[number]
export const MIDDEL_MUTATIE_BRON_LABELS: Record<MiddelMutatieBron, string> = {
  HANDMATIG: 'Handmatig',
  ONBOARDING: 'Onboarding',
  OFFBOARDING: 'Uit dienst',
  BRUIKLEEN: 'Bruikleen',
  VOERTUIGWISSEL: 'Voertuigwissel',
  LEVERING: 'Levering',
  IMPORT: 'Import',
  KIST: 'Kar of koffer',
  TAAK: 'Taak',
  FORMULIER: 'Formulier',
  REPARATIE: 'Reparatie',
  SYSTEEM: 'Systeem',
  SAMENSTELLING: 'Samenstel-opdracht',
  WELKOMST: 'Welkomstpakket',
  KOFFER_SET: 'Koffer als set',
  INNAME_SESSIE: 'Innamelijst',
  TELLING: 'Telling',
  CAMPAGNE: 'Uitrustingscontrole',
}

export const MIDDEL_FORMULIER_MODI = ['UITGIFTE', 'INNAME', 'OVERDRACHT', 'WISSEL'] as const
export type MiddelFormulierModus = (typeof MIDDEL_FORMULIER_MODI)[number]
export const MIDDEL_FORMULIER_MODUS_LABELS: Record<MiddelFormulierModus, string> = {
  UITGIFTE: 'Uitgifte',
  INNAME: 'Inname',
  OVERDRACHT: 'Overdracht',
  WISSEL: 'Wissel',
}

/**
 * Afboeken bij inname: KAPOT (het middel is terug maar niet meer bruikbaar; een
 * bewijsfoto is verplicht) of VERMIST (het middel is er niet; een foto kan dus
 * niet en wordt niet gevraagd). In beide gevallen is de reden verplicht.
 */
export const MIDDEL_AFBOEK_SOORTEN = ['KAPOT', 'VERMIST'] as const
export type MiddelAfboekSoort = (typeof MIDDEL_AFBOEK_SOORTEN)[number]
export const MIDDEL_AFBOEK_SOORT_LABELS: Record<MiddelAfboekSoort, string> = {
  KAPOT: 'Kapot',
  VERMIST: 'Kwijt',
}

/**
 * Is bij deze inname een bewijsfoto verplicht? (FO-499 §5.3: bij een staat
 * anders dan GOED.) Eén regel voor schema, service en formulier.
 */
export function middelFotoVerplicht(regel: {
  conditie?: string | null
  afboeken?: boolean
  afboekSoort?: MiddelAfboekSoort | null
}): boolean {
  if (regel.afboeken) return regel.afboekSoort === 'KAPOT'
  return Boolean(regel.conditie) && regel.conditie !== 'GOED'
}

export const MIDDEL_FORMULIER_REGEL_ACTIES = ['UITGEGEVEN', 'INGENOMEN', 'OVERGEDRAGEN', 'AFGEBOEKT', 'BEVESTIGD'] as const
export type MiddelFormulierRegelActie = (typeof MIDDEL_FORMULIER_REGEL_ACTIES)[number]

export const MIDDEL_OVERDRACHT_STATUSSEN = ['WACHT_OP_BEVESTIGING', 'UITGEVOERD', 'GEWEIGERD', 'VERVALLEN'] as const
export type MiddelOverdrachtStatus = (typeof MIDDEL_OVERDRACHT_STATUSSEN)[number]
export const MIDDEL_OVERDRACHT_STATUS_LABELS: Record<MiddelOverdrachtStatus, string> = {
  WACHT_OP_BEVESTIGING: 'Wacht op bevestiging',
  UITGEVOERD: 'Uitgevoerd',
  GEWEIGERD: 'Geweigerd',
  VERVALLEN: 'Vervallen',
}

/** Status van de bruikleenovereenkomst die een middel dekt (portal- en tab-tijdlijn). */
export const MIDDEL_BRUIKLEEN_STATUSSEN = ['GEEN', 'OPEN_TAAK', 'WACHT_OP_ONDERTEKENING', 'ONDERTEKEND', 'BEEINDIGD'] as const
export type MiddelBruikleenStatus = (typeof MIDDEL_BRUIKLEEN_STATUSSEN)[number]
export const MIDDEL_BRUIKLEEN_STATUS_LABELS: Record<MiddelBruikleenStatus, string> = {
  GEEN: 'Geen overeenkomst',
  OPEN_TAAK: 'Overeenkomst nog op te stellen',
  WACHT_OP_ONDERTEKENING: 'Wacht op ondertekening',
  ONDERTEKEND: 'Ondertekend',
  BEEINDIGD: 'Beëindigd',
}

// ─── Ledger-DTO's ─────────────────────────────────────────────────────────────

export interface MiddelMutatieDTO {
  id: string
  middelType: MiddelType
  middelId: string
  aantal: number
  omschrijving: string
  kenmerk: string | null
  soort: MiddelMutatieSoort
  bron: MiddelMutatieBron
  vanEmployeeId: string | null
  vanEmployeeNaam: string | null
  naarEmployeeId: string | null
  naarEmployeeNaam: string | null
  vanLocatieNaam: string | null
  naarLocatieNaam: string | null
  /** ISO-datetime. */
  moment: string
  actorNaam: string | null
  reden: string | null
  conditie: OffboardingInleverStaat | null
  fotoAantal: number
  sessieId: string | null
  /** Document-id van de PDF-bevestiging uit de formulier-sessie, of null. */
  bevestigingDocumentId: string | null
}

/** Eén middel met zijn tijdlijn en de status van de bruikleenovereenkomst. */
export interface MiddelTijdlijnDTO {
  middelType: MiddelType
  middelId: string
  omschrijving: string
  kenmerk: string | null
  /** Huidige houder volgens de ledger (null = voorraad/afgeboekt). */
  huidigeEmployeeId: string | null
  bruikleen: {
    status: MiddelBruikleenStatus
    documentId: string | null
    /** ISO-datetime van de ondertekening of beëindiging, of null. */
    moment: string | null
  }
  /** Eén pagina van de tijdlijn, nieuwste eerst. */
  mutaties: MiddelMutatieDTO[]
  /** Totaal aantal bewegingen van dit middel (over alle pagina's). */
  totaal: number
}

/** Paginagrootte van de middelen-tijdlijn (medewerker en middel). */
export const MIDDEL_TIJDLIJN_PAGINA = 25

/** Open bezit volgens de ledger: de laatste beweging per middel laat het bij de medewerker. */
export interface MiddelOpenBezitDTO {
  middelType: MiddelType
  middelId: string
  omschrijving: string
  kenmerk: string | null
  aantal: number
  /** ISO-datetime van de uitgifte/overdracht. */
  uitgegevenOp: string
  bron: MiddelMutatieBron
  /** Document-id van het uitgiftedocument (formulier-bevestiging), of null. */
  uitgifteDocumentId: string | null
  /** Aantal foto's van destijds (op de uitgifte-mutatie). */
  fotoAantal: number
  mutatieId: string
  /**
   * Verbruiksmateriaal of kantoorartikel: is verbruikt en wordt normaal niet
   * teruggevraagd. Het formulier zet het daarom niet vanzelf op de innamelijst.
   */
  verbruik?: boolean
  /**
   * batch-69 samenstellen (FO-510 §C): eigendom van de medewerker (merchandise,
   * welkomstpakket). Geen bruikleen, nooit op de inleverlijst; wel zichtbaar.
   */
  eigendom?: boolean
  /** Set-artikel (BULK_ARTIKEL): de vaste inhoud per stuk, bv. wat er in een PBM-koffer zit. */
  inhoud?: VasteInhoudRegelDTO[]
}

/** Open bezit van een medewerker, met de status van de bruikleenovereenkomst per middel. */
export interface MiddelBezitDTO extends MiddelOpenBezitDTO {
  bruikleen: MiddelTijdlijnDTO['bruikleen']
  /** true = alleen bekend uit de bron-tabel (bezit van vóór het ledger): geen uitgifte-mutatie. */
  vangnet: boolean
}

/** Het middelenoverzicht van een medewerker: wat hij heeft en wat er wanneer mee gebeurde. */
export interface MiddelenOverzichtDTO {
  /** De medewerker om wie het gaat (naam voor het formulier en de koppen). */
  employeeId: string
  employeeNaam: string
  bezit: MiddelBezitDTO[]
  /** De eerste pagina van de tijdlijn (nieuwste eerst); de rest via het tijdlijn-endpoint. */
  tijdlijn: MiddelMutatieDTO[]
  /** Totaal aantal bewegingen van deze medewerker. */
  tijdlijnTotaal: number
  /** Overdrachten die op bevestiging wachten (als afgever of ontvanger) plus de recent afgehandelde. */
  overdrachten: MiddelOverdrachtVerzoekDTO[]
}

// ─── Formulier (gedeeld component + API) ──────────────────────────────────────

/**
 * Verwijzing naar een vooraf geuploade bewijsfoto: een door de client gekozen
 * UUID (`POST /middelen/fotos?fotoId=...`). Zo blijft elk verzoek klein (één foto
 * per upload, goed op slechte 4G), is een herhaalde upload idempotent en kan de
 * portal foto's en formulier los van elkaar in de offline-wachtrij zetten: de
 * foto's eerst, het formulier erna (FIFO). De server zoekt de foto op onder de
 * map van de ingelogde gebruiker; een id van iemand anders bestaat daar niet.
 */
export const MiddelFotoIdSchema = z.string().uuid('Ongeldige foto-verwijzing')

/**
 * Voorraad-categorieën waarvoor een losse uitgifte een verwachte retourdatum
 * vraagt (spec 30 deel 9). Eén lijst voor de API (`uitgevenUniek`) en het
 * formulier, zodat het veld precies verschijnt waar de server het eist.
 */
export const MIDDEL_RETOURDATUM_VERPLICHT_CATEGORIEEN = ['TELEFOON', 'LAPTOP', 'TABLET', 'AUTOSLEUTEL'] as const

/** Eén zoekresultaat in de middel-picker van het formulier. */
export interface MiddelZoekOptieDTO {
  middelType: MiddelType
  middelId: string
  omschrijving: string
  kenmerk: string | null
  /** Korte context ("Op voorraad: Magazijn Bergen", "12 stuks vrij op voorraad"). */
  detail: string | null
  /** Voorraad-categorie (uniek/bulk), of null bij een voertuig. */
  categorie: string | null
}

/** Een collega in de overdracht-picker van de portal. */
export interface MiddelCollegaOptieDTO {
  employeeId: string
  naam: string
  functie: string | null
}

/**
 * Wat de ingelogde gebruiker met middelen mag (UI-hint; de server blijft de poort).
 * De rechten gelden per middeltype en per actie: wie kleding mag uitgeven, mag
 * daarmee nog geen voertuig toewijzen, en innemen is ruimer dan uitgeven (HR mag
 * altijd innemen).
 */
export interface MiddelenRechtenDTO {
  /** Mag van ten minste één type het bezit van medewerkers inzien. */
  inzien: boolean
  /** De middeltypes waarvan hij het bezit van medewerkers mag inzien. */
  inzienbareTypes: MiddelType[]
  /** De middeltypes die hij mag uitgeven en overdragen. */
  beheerbareTypes: MiddelType[]
  /** De middeltypes die hij mag innemen. */
  inneembareTypes: MiddelType[]
  /** De middeltypes die hij mag afboeken (kapot of kwijt). */
  afboekbareTypes: MiddelType[]
  instellen: boolean
}

/** Welke types mag deze gebruiker op het formulier zetten, gegeven de richting van de regel? */
export function middelTypesVoorRichting(
  rechten: Pick<MiddelenRechtenDTO, 'beheerbareTypes' | 'inneembareTypes'>,
  richting: 'IN' | 'UIT' | 'OVER',
): MiddelType[] {
  return richting === 'IN' ? rechten.inneembareTypes : rechten.beheerbareTypes
}

/** Eén regel op het formulier zoals de client hem instuurt. */
export const MiddelFormulierRegelInputSchema = z.object({
  /** Client-sleutel van de regel (uniek binnen het formulier; komt terug in `fouten[]`). */
  key: z.string().trim().min(1).max(64),
  middelType: z.enum(MIDDEL_TYPES),
  middelId: idParam('middelId'),
  aantal: z.number().int().min(1).max(10_000).default(1),
  /** Staat bij inname/overdracht; bij uitgifte optioneel (nieuwstaat). */
  conditie: z.enum(OFFBOARDING_INLEVER_STATEN).nullable().optional(),
  opmerking: z.string().trim().max(2000).nullable().optional(),
  /** INNAME: kapot/vermist afboeken in plaats van terug op voorraad (reden verplicht). */
  afboeken: z.boolean().optional(),
  /** Bij afboeken: KAPOT (foto verplicht) of VERMIST (geen foto). */
  afboekSoort: z.enum(MIDDEL_AFBOEK_SOORTEN).nullable().optional(),
  afboekReden: z.string().trim().max(2000).nullable().optional(),
  /** INNAME van een uniek item: doellocatie (magazijn); bij afboeken niet nodig. */
  locatieId: idParam('locatieId').nullable().optional(),
  /** De vooraf geuploade bewijsfoto's van deze regel (zie `MiddelFotoIdSchema`). */
  fotoIds: z.array(MiddelFotoIdSchema).max(10, "Maximaal 10 foto's per middel.").default([]),
  /**
   * Verwachte retourdatum (ISO-dag) bij de uitgifte van een uniek exemplaar.
   * Verplicht voor de retourplichtige categorieën (zie
   * `MIDDEL_RETOURDATUM_VERPLICHT_CATEGORIEEN`), behalve voor de hoofdsleutel bij
   * de vaste bestuurder; de API dwingt het af.
   */
  verwachteRetour: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd')
    .nullable()
    .optional(),
  /**
   * Alleen bij modus WISSEL (zelfde medewerker): IN = het oude middel dat
   * terugkomt, UIT = het nieuwe middel dat hij meekrijgt. Bij de andere modi
   * volgt de richting uit de modus.
   */
  richting: z.enum(['IN', 'UIT']).optional(),
})
export type MiddelFormulierRegelInput = z.infer<typeof MiddelFormulierRegelInputSchema>

/** De richting van een regel: uit de modus, of bij WISSEL uit de regel zelf. */
export function middelRegelRichting(
  modus: MiddelFormulierModus,
  regel: { richting?: 'IN' | 'UIT' },
): 'IN' | 'UIT' | 'OVER' {
  if (modus === 'UITGIFTE') return 'UIT'
  if (modus === 'INNAME') return 'IN'
  if (modus === 'OVERDRACHT') return 'OVER'
  return regel.richting ?? 'IN'
}

const handtekening = z
  .string()
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Handtekening ontbreekt of is ongeldig')
  .max(2_000_000)

export const MiddelFormulierSubmitSchema = z
  .object({
    modus: z.enum(MIDDEL_FORMULIER_MODI),
    employeeId: idParam('employeeId'),
    /** OVERDRACHT: de collega die het middel overneemt. */
    tegenpartijEmployeeId: idParam('tegenpartijEmployeeId').nullable().optional(),
    bron: z.enum(MIDDEL_MUTATIE_BRONNEN).default('FORMULIER'),
    /** Onboarding-id of offboarding-workflow-id. */
    bronReferentie: z.string().trim().max(64).nullable().optional(),
    /** ISO-datetime van het moment; leeg = nu. */
    moment: z.string().datetime().nullable().optional(),
    plek: z.string().trim().max(200).nullable().optional(),
    opmerking: z.string().trim().max(2000).nullable().optional(),
    /** Naam van de ontvanger zoals getekend (default: de medewerkernaam). */
    ontvangerNaam: z.string().trim().max(120).nullable().optional(),
    handtekeningAfgever: handtekening.nullable().optional(),
    handtekeningOntvanger: handtekening.nullable().optional(),
    regels: z.array(MiddelFormulierRegelInputSchema).min(1, 'Voeg minimaal een middel toe.').max(200),
    idempotencyKey: z.string().trim().min(8).max(200).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    // FO-499 §5.3: handtekening ontvanger verplicht bij uitgifte (en bij een
    // overdracht: de collega tekent voor ontvangst; bij een wissel tekent de
    // medewerker als ontvanger voor inleveren en meekrijgen samen), handtekening
    // afgever verplicht bij inname (en bij een overdracht: wie afgeeft tekent).
    if (val.modus !== 'INNAME' && !val.handtekeningOntvanger) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['handtekeningOntvanger'],
        message: 'De ontvanger moet tekenen voor ontvangst.',
      })
    }
    if ((val.modus === 'INNAME' || val.modus === 'OVERDRACHT') && !val.handtekeningAfgever) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['handtekeningAfgever'],
        message:
          val.modus === 'INNAME'
            ? 'De medewerker die inlevert moet tekenen voor de inname.'
            : 'De medewerker die het middel afgeeft moet tekenen.',
      })
    }
    if (val.modus === 'OVERDRACHT' && !val.tegenpartijEmployeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tegenpartijEmployeeId'],
        message: 'Kies aan wie het middel wordt overgedragen.',
      })
    }
    if (val.tegenpartijEmployeeId && val.tegenpartijEmployeeId === val.employeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tegenpartijEmployeeId'],
        message: 'Een middel kan niet aan dezelfde medewerker worden overgedragen.',
      })
    }
    const keys = new Set<string>()
    val.regels.forEach((r, i) => {
      if (keys.has(r.key)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['regels', i, 'key'], message: 'Dubbele regel.' })
      }
      keys.add(r.key)
      // FO-499 §5.3: foto's verplicht bij een staat anders dan GOED (en bij
      // afboeken als kapot; een vermist middel valt niet te fotograferen).
      if (middelFotoVerplicht(r) && r.fotoIds.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'fotoIds'],
          message: r.afboeken ? 'Maak minimaal een foto van het kapotte middel.' : 'Maak minimaal een foto bij een staat anders dan goed.',
        })
      }
      const richting = middelRegelRichting(val.modus, r)
      if (val.modus === 'WISSEL' && !r.richting) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'richting'],
          message: 'Geef aan of dit middel terugkomt of wordt meegegeven.',
        })
      }
      if (richting !== 'UIT' && !r.afboeken && !r.conditie) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'conditie'],
          message: 'Geef de staat op waarin het middel is ingeleverd.',
        })
      }
      if (r.afboeken && !r.afboekSoort) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'afboekSoort'],
          message: 'Kies of het middel kapot of kwijt is.',
        })
      }
      if (richting !== 'IN' && r.afboeken) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'afboeken'],
          message: 'Afboeken kan alleen bij een middel dat wordt ingenomen.',
        })
      }
      if (r.afboeken && (!r.afboekReden || r.afboekReden.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'afboekReden'],
          message: 'Reden is verplicht bij afboeken (kwijt of kapot).',
        })
      }
      const locatieNodig =
        !r.afboeken &&
        ((r.middelType === 'BULK_ARTIKEL' && richting !== 'OVER') ||
          (r.middelType === 'VOORRAAD_UNIEK_ITEM' && richting === 'IN'))
      if (locatieNodig && !r.locatieId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['regels', i, 'locatieId'],
          message:
            r.middelType === 'BULK_ARTIKEL'
              ? 'Kies de voorraadlocatie voor dit artikel.'
              : 'Kies de locatie waar het middel teruggelegd wordt.',
        })
      }
    })
  })
export type MiddelFormulierSubmitInput = z.infer<typeof MiddelFormulierSubmitSchema>
/** Wat een client instuurt (vóór de defaults van het schema). */
export type MiddelFormulierSubmitPayload = z.input<typeof MiddelFormulierSubmitSchema>

export interface MiddelFormulierRegelDTO {
  id: string
  /** Leeg bij een regel zonder geregistreerd middel (handmatige inlever-regel). */
  middelType: MiddelType | null
  middelId: string | null
  aantal: number
  omschrijving: string
  kenmerk: string | null
  actie: MiddelFormulierRegelActie
  conditie: OffboardingInleverStaat | null
  opmerking: string | null
  fotoAantal: number
  afboekReden: string | null
  mutatieId: string | null
}

export interface MiddelFormulierSessieDTO {
  id: string
  modus: MiddelFormulierModus
  status: 'VERWERKT' | 'WACHT_OP_BEVESTIGING' | 'GEANNULEERD'
  bron: MiddelMutatieBron
  bronReferentie: string | null
  employeeId: string
  employeeNaam: string | null
  tegenpartijEmployeeId: string | null
  tegenpartijNaam: string | null
  /** ISO-datetime. */
  moment: string
  plek: string | null
  opmerking: string | null
  afgeverNaam: string | null
  ontvangerNaam: string | null
  heeftHandtekeningAfgever: boolean
  heeftHandtekeningOntvanger: boolean
  bevestigingDocumentId: string | null
  bevestigingVersieNr: number | null
  regels: MiddelFormulierRegelDTO[]
}

export interface MiddelFormulierResultaatDTO {
  sessieId: string
  verwerkt: number
  bevestigingDocumentId: string | null
  bevestigingVersieNr: number | null
  /** Regels waarvan de bron-mutatie faalde (de rest is wel verwerkt). */
  fouten: Array<{ key: string; code: string; message: string }>
  /** FO-505 §12.4: ontbrekende artikelen t.o.v. het pakket na een innamesessie. */
  ontbrekend: MiddelOntbrekendArtikelDTO[]
}

/** Een artikel uit het pakket van de medewerker dat na de inname niet (volledig) terug is. */
export interface MiddelOntbrekendArtikelDTO {
  voorraadArtikelId: string
  artikelNaam: string
  aantalVerwacht: number
  aantalIngeleverd: number
  aantalOntbreekt: number
  /** Vrij op voorraad (alle locaties). */
  vrijOpVoorraad: number
  /** Voorstelwaarde per stuk (Decimal-string) voor doorbelasten, of null als geen prijs bekend is. */
  voorstelPerStuk: string | null
}

// ─── Overdracht ───────────────────────────────────────────────────────────────

/** Directe overdracht (backoffice: medewerker-tab, item-detail). */
export const MiddelOverdrachtInputSchema = z
  .object({
    middelType: z.enum(MIDDEL_TYPES),
    middelId: idParam('middelId'),
    naarEmployeeId: idParam('naarEmployeeId'),
    reden: z.string().trim().max(2000).nullable().optional(),
    conditie: z.enum(OFFBOARDING_INLEVER_STATEN).nullable().optional(),
    /** Bewijsfoto's van de staat bij de overdracht (verplicht bij een staat anders dan GOED). */
    fotoIds: z.array(MiddelFotoIdSchema).max(10).optional(),
    /** Voertuig: VAST of TIJDELIJK voor de nieuwe toewijzing (default VAST). */
    toewijzingSoort: z.enum(['VAST', 'TIJDELIJK']).optional(),
    bron: z.enum(MIDDEL_MUTATIE_BRONNEN).default('HANDMATIG'),
    bronReferentie: z.string().trim().max(64).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.middelType === 'BULK_ARTIKEL' || val.middelType === 'TANKPAS') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['middelType'],
        message: 'Bulk-artikelen en tankpassen worden niet overgedragen; neem in en geef opnieuw uit.',
      })
    }
    if (middelFotoVerplicht({ conditie: val.conditie }) && (val.fotoIds ?? []).length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fotoIds'],
        message: 'Voeg minstens één foto toe als de staat niet goed is.',
      })
    }
  })
export type MiddelOverdrachtInput = z.infer<typeof MiddelOverdrachtInputSchema>
export type MiddelOverdrachtPayload = z.input<typeof MiddelOverdrachtInputSchema>

/** Overdracht gestart door de houder in de portal; de ontvanger bevestigt. */
export const MiddelOverdrachtVerzoekInputSchema = z.object({
  middelType: z.enum(['VOORRAAD_UNIEK_ITEM', 'UITRUSTING_ITEM', 'VOERTUIG']),
  middelId: idParam('middelId'),
  naarEmployeeId: idParam('naarEmployeeId'),
  reden: z.string().trim().max(2000).nullable().optional(),
  handtekeningAfgever: handtekening,
})
export type MiddelOverdrachtVerzoekInput = z.infer<typeof MiddelOverdrachtVerzoekInputSchema>

export const MiddelOverdrachtBevestigSchema = z
  .object({
    besluit: z.enum(['BEVESTIGEN', 'WEIGEREN']),
    handtekeningOntvanger: handtekening.nullable().optional(),
    conditie: z.enum(OFFBOARDING_INLEVER_STATEN).nullable().optional(),
    /** Bewijsfoto's van de staat bij ontvangst (verplicht bij een staat anders dan GOED). */
    fotoIds: z.array(MiddelFotoIdSchema).max(10).optional(),
    weigerReden: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.besluit === 'BEVESTIGEN' && !val.handtekeningOntvanger) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['handtekeningOntvanger'],
        message: 'Teken voor ontvangst om de overdracht te bevestigen.',
      })
    }
    if (val.besluit === 'BEVESTIGEN' && middelFotoVerplicht({ conditie: val.conditie }) && (val.fotoIds ?? []).length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fotoIds'],
        message: 'Voeg minstens één foto toe als de staat niet goed is.',
      })
    }
    if (val.besluit === 'WEIGEREN' && (!val.weigerReden || val.weigerReden.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['weigerReden'], message: 'Geef een reden op.' })
    }
  })
export type MiddelOverdrachtBevestigInput = z.infer<typeof MiddelOverdrachtBevestigSchema>
export type MiddelOverdrachtBevestigPayload = z.input<typeof MiddelOverdrachtBevestigSchema>
export type MiddelOverdrachtVerzoekPayload = z.input<typeof MiddelOverdrachtVerzoekInputSchema>

export interface MiddelOverdrachtVerzoekDTO {
  id: string
  middelType: MiddelType
  middelId: string
  omschrijving: string
  kenmerk: string | null
  vanEmployeeId: string
  vanEmployeeNaam: string | null
  naarEmployeeId: string
  naarEmployeeNaam: string | null
  status: MiddelOverdrachtStatus
  reden: string | null
  /** ISO-datetime. */
  aangemaaktOp: string
  beslistOp: string | null
  weigerReden: string | null
}

export interface MiddelOverdrachtResultaatDTO {
  /** De formulier-sessie met de handtekeningen, of null bij een directe overdracht zonder formulier. */
  sessieId: string | null
  /** De twee ledgerregels (inname-kant en uitgifte-kant). */
  mutatieIds: [string, string]
  /** Bruikleenovereenkomsten die bij de overdracht zijn beëindigd. */
  beeindigdeBruikleenDocumentIds: string[]
  /** Nieuwe bruikleenovereenkomst voor de ontvanger (document-id), of null als de taak-terugval greep of de generatie nog loopt. */
  nieuweBruikleenDocumentId: string | null
  /** Abonnementen die zijn meegegaan (FO-463). */
  meegekoppeldeAbonnementen: number
}

// ─── Innemen / afboeken altijd mogelijk (FO-499 §A3) ──────────────────────────

export const MiddelInnemenInputSchema = z
  .object({
    middelType: z.enum(MIDDEL_TYPES),
    middelId: idParam('middelId'),
    /** De staat waarin het middel terugkomt; niet van toepassing bij afboeken. */
    conditie: z.enum(OFFBOARDING_INLEVER_STATEN).nullable().optional(),
    reden: z.string().trim().max(2000).nullable().optional(),
    /** Uniek item: doellocatie (verplicht tenzij afboeken). Bulk: locatie van de retour. */
    locatieId: idParam('locatieId').nullable().optional(),
    aantal: z.number().int().min(1).max(10_000).default(1),
    /** Kapot of vermist: afboeken in plaats van terug op voorraad (reden verplicht). */
    afboeken: z.boolean().default(false),
    /** Bij afboeken: KAPOT (foto verplicht) of VERMIST (geen foto). */
    afboekSoort: z.enum(MIDDEL_AFBOEK_SOORTEN).nullable().optional(),
    /** Vooraf geuploade bewijsfoto's (verplicht minimaal een bij een staat anders dan GOED). */
    fotoIds: z.array(MiddelFotoIdSchema).max(10, "Maximaal 10 foto's per middel.").default([]),
    /** Bulk-artikel: van welke medewerker de retour is (bij de andere types volgt de houder uit het middel). */
    employeeId: idParam('employeeId').nullable().optional(),
    bron: z.enum(MIDDEL_MUTATIE_BRONNEN).default('HANDMATIG'),
  })
  .superRefine((val, ctx) => {
    if (!val.afboeken && !val.conditie) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['conditie'], message: 'Geef de staat op waarin het middel is ingeleverd.' })
    }
    if (val.afboeken && !val.afboekSoort) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['afboekSoort'], message: 'Kies of het middel kapot of kwijt is.' })
    }
    if (middelFotoVerplicht(val) && val.fotoIds.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fotoIds'],
        message: val.afboeken ? 'Maak minimaal een foto van het kapotte middel.' : 'Maak minimaal een foto bij een staat anders dan goed.',
      })
    }
    if (val.middelType === 'BULK_ARTIKEL' && !val.employeeId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['employeeId'], message: 'Kies van welke medewerker de retour is.' })
    }
    if (val.afboeken && (!val.reden || val.reden.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reden'], message: 'Reden is verplicht bij afboeken.' })
    }
    if (!val.afboeken && (val.middelType === 'VOORRAAD_UNIEK_ITEM' || val.middelType === 'BULK_ARTIKEL') && !val.locatieId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['locatieId'], message: 'Kies een doellocatie.' })
    }
  })
export type MiddelInnemenInput = z.infer<typeof MiddelInnemenInputSchema>
export type MiddelInnemenPayload = z.input<typeof MiddelInnemenInputSchema>

// ─── Toewijzen zonder voorraad ────────────────────────────────────────────────
// Kleding, PBM of gereedschap dat van buiten het magazijn komt (al in bezit,
// elders gekocht, rechtstreeks geleverd) op naam van een medewerker zetten.
// Het magazijn blijft ongemoeid: geen reservering, geen afboeking, geen
// telverschil en geen credits. Het bezit draagt de vaste markering
// "geregistreerd zonder voorraadmutatie" (FO-381), en innemen en offboarding
// werken er gewoon op.

/** yyyy-mm-dd die als kalenderdag bestaat (geen 31-02 of 2026-13-01). */
function isBestaandeYmd(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return false
  const [j, mnd, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const dt = new Date(Date.UTC(j, mnd - 1, d))
  return dt.getUTCFullYear() === j && dt.getUTCMonth() === mnd - 1 && dt.getUTCDate() === d
}

/**
 * Sinds wanneer de medewerker het heeft (yyyy-mm-dd): een bestaande dag en niet
 * na vandaag in Europe/Amsterdam. Los geëxporteerd zodat het formulier exact
 * dezelfde regel toetst als de API.
 */
export const MiddelUitgereiktOpSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum.')
  .refine(isBestaandeYmd, 'Deze datum bestaat niet.')
  .refine((v) => v <= amsterdamVandaagYmd(), 'De datum kan niet in de toekomst liggen.')

export const MIDDEL_TOEWIJZING_CATEGORIEEN = ['KLEDING', 'PBM', 'GEREEDSCHAP'] as const
export type MiddelToewijzingCategorie = (typeof MIDDEL_TOEWIJZING_CATEGORIEEN)[number]

export const MIDDEL_TOEWIJZING_CATEGORIE_LABELS: Record<MiddelToewijzingCategorie, string> = {
  KLEDING: 'Kleding',
  PBM: 'PBM',
  GEREEDSCHAP: 'Gereedschap',
}

export const MiddelToewijzenZonderVoorraadSchema = z
  .object({
    employeeId: idParam('employeeId'),
    /** Catalogus-artikel; leeg = vrije omschrijving. */
    uitrustingArtikelId: idParam('uitrustingArtikelId').nullable().optional(),
    /** Maat-variant van het catalogus-artikel (verplicht bij meerdere maten). */
    variantId: idParam('variantId').nullable().optional(),
    /** Alleen zonder catalogus-artikel: wat het is. */
    omschrijving: z.string().trim().max(200).nullable().optional(),
    /** Alleen zonder catalogus-artikel: het soort middel. */
    categorie: z.enum(MIDDEL_TOEWIJZING_CATEGORIEEN).nullable().optional(),
    /** Alleen zonder catalogus-artikel: de maat (vrij, het stuk wordt nergens tegen gematcht). */
    maat: z.string().trim().max(40).nullable().optional(),
    aantal: z.number().int().min(1, 'Minimaal 1 stuk.').max(50, 'Maximaal 50 stuks per keer.').default(1),
    /** Sinds wanneer de medewerker het heeft (yyyy-mm-dd); leeg = vandaag. */
    uitgereiktOp: MiddelUitgereiktOpSchema.nullable().optional(),
    /**
     * Verplicht: dit pad schrijft bewust geen credits af (PO 2026-10-23: het is
     * registratie van wat de medewerker al heeft), dus de audit moet zeggen waarom.
     */
    reden: z
      .string({ error: 'Geef een reden op.' })
      .trim()
      .min(5, 'Geef een reden op (minimaal 5 tekens).')
      .max(2000),
  })
  .superRefine((val, ctx) => {
    if (val.uitrustingArtikelId) return
    if (!val.omschrijving) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['omschrijving'],
        message: 'Kies een artikel uit de catalogus of vul een omschrijving in.',
      })
    }
    if (!val.categorie) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['categorie'], message: 'Kies het soort middel.' })
    }
  })
export type MiddelToewijzenZonderVoorraadInput = z.infer<typeof MiddelToewijzenZonderVoorraadSchema>
export type MiddelToewijzenZonderVoorraadPayload = z.input<typeof MiddelToewijzenZonderVoorraadSchema>

/** Catalogus-artikel voor de toewijzen-picker (geen prijzen, geen voorraad). */
export interface MiddelCatalogusArtikelDTO {
  id: string
  naam: string
  merk: string | null
  categorie: string
  varianten: Array<{ id: string; maat: string | null; kleur: string | null }>
}

export interface MiddelToewijzingResultaatDTO {
  uitrustingItemIds: string[]
  omschrijving: string
}

// ─── Instellingen ─────────────────────────────────────────────────────────────

export const MiddelenInstellingenSchema = z
  .object({
  autoBruikleenVoertuig: z.boolean(),
  autoBruikleenTelefoonSim: z.boolean(),
  autoBruikleenGereedschapSet: z.boolean(),
  autoBruikleenLaptop: z.boolean(),
  autoBruikleenSleutel: z.boolean(),
  bruikleenHandmatigeControle: z.boolean(),
  ondertekeningHerinneringDagen: z.number().int().min(1).max(60),
  ondertekeningEscalatieDagen: z.number().int().min(1).max(120),
  /** Vast bruikleensjabloon per triggersoort; null = het enige passende sjabloon (anders de taak-terugval). */
  sjabloonVoertuigMetBijtellingId: idParam('sjabloonId').nullable(),
  sjabloonVoertuigZonderBijtellingId: idParam('sjabloonId').nullable(),
  sjabloonTelefoonSimId: idParam('sjabloonId').nullable(),
  sjabloonGereedschapSetId: idParam('sjabloonId').nullable(),
  sjabloonLaptopId: idParam('sjabloonId').nullable(),
  sjabloonSleutelId: idParam('sjabloonId').nullable(),
  afschrijvingTelefoonMaanden: z.number().int().min(1).max(240),
  afschrijvingElektronicaMaanden: z.number().int().min(1).max(240),
  afschrijvingGereedschapMaanden: z.number().int().min(1).max(240),
  afschrijvingKledingMaanden: z.number().int().min(1).max(240),
  afschrijvingOverigMaanden: z.number().int().min(1).max(240),
  /** Percentage 0-100 als Decimal-string ("10.00"). */
  minimumRestwaardePct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/),
  reparatieGoedkeurderUserIds: z.array(idParam('userId')).max(50),
  verhaalVierOgenBoven: z.string().regex(/^\d{1,8}(\.\d{1,2})?$/),
  verrekenWaarschuwingPct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/),
  bezwaartermijnDagen: z.number().int().min(1).max(90),
  /**
   * batch-69 samenstellen (FO-516 §4) - de doelgroep "inname-bevoegd": wie in de
   * portal een innamelijst opent, afvinkt en afsluit. Weggelaten = ongewijzigd;
   * leeg = terugval op magazijnbeheer (voorraad goedkeuren) en ADMIN.
   */
  innameBevoegdUserIds: z.array(idParam('userId')).max(50).optional(),
  /**
   * batch-70 uitrusting-controle (FO-531 §D11) - de ontvangst- en gebruiksverklaring
   * kleding en PBM. Weggelaten = ongewijzigd (oudere formulieren).
   */
  autoBruikleenKledingPbm: z.boolean().optional(),
  sjabloonKledingPbmId: idParam('sjabloonId').nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.ondertekeningEscalatieDagen <= val.ondertekeningHerinneringDagen) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ondertekeningEscalatieDagen'],
        message: 'De escalatie moet later vallen dan de herinnering.',
      })
    }
    for (const veld of ['minimumRestwaardePct', 'verrekenWaarschuwingPct'] as const) {
      if (Number(val[veld]) > 100) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [veld], message: 'Een percentage is hooguit 100.' })
      }
    }
  })
export type MiddelenInstellingenInput = z.infer<typeof MiddelenInstellingenSchema>

export interface MiddelenInstellingenDTO extends MiddelenInstellingenInput {
  /** Namen van de ingestelde goedkeurders (zelfde volgorde als de id's). */
  reparatieGoedkeurderNamen: string[]
  /** batch-69 samenstellen (FO-516): de doelgroep "inname-bevoegd" en hun namen. */
  innameBevoegdUserIds: string[]
  innameBevoegdNamen: string[]
  /** batch-70 uitrusting-controle (FO-531 §D11): kleding en PBM. */
  autoBruikleenKledingPbm: boolean
  sjabloonKledingPbmId: string | null
}

/** De afschrijvingstabel zoals `stelWaardeVoor` hem leest (pure functie in @streep/utils). */
export interface InnameAfschrijvingstabel {
  telefoonMaanden: number
  elektronicaMaanden: number
  gereedschapMaanden: number
  kledingMaanden: number
  overigMaanden: number
  /** 0-100 als Decimal-string. */
  minimumRestwaardePct: string
}
